import * as joint from '@joint/core';
import { applyConnectorStyleMixin } from './constants';
import { Callout, DrawnLine, IconElement, TextBox } from './drawable-elements';
import { EntityGroup } from './entity-nodes';
import { createFlowLinkTextLabel } from './flow-links';
import { createIconLabelMarkup, fetchIconShape, isIconLabel } from './icons';
import { Node } from './node-base';
import {
    classifyCell,
    isAllowedValue,
    isAutoLayoutPlaced,
    supportsProperty,
    type SnapshotCellKind,
} from './capability-registry';
import {
    ConnectorStyle,
    isBorderColorable,
    isConnectorStyleable,
    isFillColorable,
    isShapeColorable,
    isTextColorable,
} from './types';

export interface PointValue {
    x: number;
    y: number;
}

export interface SizeValue {
    width: number;
    height: number;
}

export interface PatchValue<T> {
    clear: boolean;
    value?: T | null;
}

export type FlowLinkLabelMutationDescriptor =
    | { type: 'addText'; text: string }
    | { type: 'addIcon'; iconName: string }
    | { type: 'updateText'; index: number; text: PatchValue<string> }
    | { type: 'remove'; index: number };

export interface FlowElementPatchDescriptor {
    text?: {
        primaryLabel?: PatchValue<string>;
        secondaryLabel?: PatchValue<string>;
        text?: PatchValue<string>;
    };
    presentation?: {
        backgroundColor?: PatchValue<string>;
        textColor?: PatchValue<string>;
        borderColor?: PatchValue<string>;
        shapeColor?: PatchValue<string>;
    };
    geometry?: {
        position?: PatchValue<PointValue>;
        size?: PatchValue<SizeValue>;
        tailTip?: PatchValue<PointValue>;
        sourcePoint?: PatchValue<PointValue>;
        targetPoint?: PatchValue<PointValue>;
    };
    icon?: {
        iconName?: PatchValue<string>;
    };
    line?: {
        strokeColor?: PatchValue<string>;
        strokeWidth?: PatchValue<number>;
        labels?: FlowLinkLabelMutationDescriptor[];
    };
}

export interface FlowLinkPatchDescriptor {
    connectorStyle?: PatchValue<string>;
    labels?: FlowLinkLabelMutationDescriptor[];
}

type AppliedPatch<T> = { applied: false } | { applied: true; clear: boolean; value?: T };

function readPatch<T>(patch: PatchValue<T> | null | undefined, propertyName: string): AppliedPatch<T> {
    if (patch == null) {
        return { applied: false };
    }
    if (patch.clear === true) {
        if (patch.value !== undefined && patch.value !== null) {
            throw new Error(`${propertyName} cannot specify both clear and value.`);
        }
        return { applied: true, clear: true };
    }
    if (patch.value === undefined || patch.value === null) {
        throw new Error(`${propertyName} must specify a value or clear.`);
    }
    return { applied: true, clear: false, value: patch.value };
}

function requireSupported(cell: joint.dia.Cell, capabilityId: string): SnapshotCellKind {
    const kind = classifyCell(cell);
    if (!supportsProperty(kind, capabilityId)) {
        throw new Error(`${kind} does not support ${capabilityId}.`);
    }
    return kind;
}

// A Callout persists its tail tip in element-local coordinates: the tail path, the drag handle,
// and the attachment segments are all drawn inside the element's own markup, and
// Callout.onPositionChanged shifts the stored value by the move delta so the tip stays put on the
// paper. Every other point the agent exchanges — element position, drawn-line endpoints, link
// vertices — is in graph coordinates, so tailTip is converted at this boundary in both directions.
// Without that, aiming a tail at a cell whose position was just inspected lands it at
// callout.position + target.position, far from the target.
export function tailTipToLocal(callout: Callout, graphPoint: PointValue): PointValue {
    const origin = callout.position();
    return { x: graphPoint.x - origin.x, y: graphPoint.y - origin.y };
}

export function tailTipToGraph(localPoint: PointValue, origin: PointValue): PointValue {
    return { x: localPoint.x + origin.x, y: localPoint.y + origin.y };
}

export function requireFinitePoint(value: PointValue, propertyName: string): void {
    if (!Number.isFinite(value.x) || !Number.isFinite(value.y)) {
        throw new Error(`${propertyName} requires finite x and y coordinates.`);
    }
}

export function requirePositiveSize(value: SizeValue, propertyName = 'size'): void {
    if (!Number.isFinite(value.width) || !Number.isFinite(value.height)
        || value.width <= 0 || value.height <= 0) {
        throw new Error(`${propertyName} requires positive finite width and height.`);
    }
}

function requireSetValue<T>(patch: AppliedPatch<T>, propertyName: string): T | undefined {
    if (!patch.applied) {
        return undefined;
    }
    if (patch.clear) {
        throw new Error(`${propertyName} cannot be cleared; set an explicit value instead.`);
    }
    return patch.value as T;
}

export async function setElementProperty(
    cell: joint.dia.Cell,
    capabilityId: string,
    value: string | number,
): Promise<void> {
    const kind = requireSupported(cell, capabilityId);
    if (!isAllowedValue(kind, capabilityId, String(value))) {
        throw new Error(`Unsupported ${capabilityId} '${value}'.`);
    }

    switch (capabilityId) {
        case 'primaryLabel':
        case 'text':
            if (cell instanceof Node || cell instanceof TextBox || cell instanceof Callout) {
                cell.setLabel(String(value));
                if (cell instanceof EntityGroup) {
                    cell.prop('groupLabel', String(value).trim(), { rewrite: true });
                }
                return;
            }
            break;
        case 'secondaryLabel':
            if (cell instanceof Node) {
                cell.setSecondaryLabel(String(value));
                return;
            }
            break;
        case 'backgroundColor':
            if (isFillColorable(cell)) {
                cell.setColor(String(value));
                return;
            }
            break;
        case 'textColor':
            if (isTextColorable(cell)) {
                cell.setFontColor(String(value));
                return;
            }
            break;
        case 'borderColor':
            if (isBorderColorable(cell)) {
                cell.setBorderColor(String(value));
                return;
            }
            break;
        case 'shapeColor':
            if (isShapeColorable(cell)) {
                cell.setShapeColor(String(value));
                return;
            }
            break;
        case 'iconName':
            if (cell instanceof IconElement) {
                const iconName = String(value);
                await cell.setIconName(iconName);
                return;
            }
            break;
        case 'strokeColor':
            if (cell instanceof DrawnLine) {
                cell.setStrokeColor(String(value));
                return;
            }
            break;
        case 'strokeWidth':
            if (cell instanceof DrawnLine) {
                const width = Number(value);
                if (!Number.isFinite(width) || width <= 0) {
                    throw new Error('strokeWidth must be a positive number.');
                }
                cell.setStrokeWidth(width);
                return;
            }
            break;
    }

    throw new Error(`${classifyCell(cell)} advertises ${capabilityId}, but no setter is available.`);
}

// Demotes the automatic-layout hint the way a manual drag does (flow-editor's pointer handler):
// set to false rather than removed, and never introduced on a cell that never carried it.
export function clearAutoLayoutHint(cell: joint.dia.Cell): void {
    if (cell.prop('agentAdded') === true) {
        cell.prop('agentAdded', false);
    }
}

export function setElementGeometry(cell: joint.dia.Cell, capabilityId: string, value: PointValue | SizeValue): void {
    const kind = requireSupported(cell, capabilityId);

    switch (capabilityId) {
        case 'position':
            if (cell instanceof joint.dia.Element) {
                const point = value as PointValue;
                requireFinitePoint(point, capabilityId);
                if (cell instanceof Callout) {
                    // Programmatic repositioning should match a manual body drag: move the local
                    // tail with the box. A subsequent tail-target patch, when present, runs after
                    // position and replaces the tip with its newly resolved graph point.
                    cell.prop('suppressTailTipLock', true);
                    cell.position(point.x, point.y);
                    cell.prop('suppressTailTipLock', false);
                } else {
                    cell.position(point.x, point.y);
                }
                // An explicit placement is the author's, so the shape leaves the pool automatic
                // layout re-arranges on later batches.
                if (isAutoLayoutPlaced(kind)) {
                    clearAutoLayoutHint(cell);
                }
                return;
            }
            break;
        case 'size':
            if (cell instanceof joint.dia.Element) {
                const size = value as SizeValue;
                requirePositiveSize(size, capabilityId);
                cell.resize(size.width, size.height);
                return;
            }
            break;
        case 'tailTip':
            if (cell instanceof Callout) {
                const point = value as PointValue;
                requireFinitePoint(point, capabilityId);
                // Supplied in graph coordinates, persisted local to the callout.
                cell.setTailTip(tailTipToLocal(cell, point));
                return;
            }
            break;
        case 'sourcePoint':
        case 'targetPoint':
            if (cell instanceof DrawnLine) {
                const point = value as PointValue;
                requireFinitePoint(point, capabilityId);
                if (capabilityId === 'sourcePoint') {
                    cell.source(point);
                } else {
                    cell.target(point);
                }
                return;
            }
            break;
    }

    throw new Error(`${classifyCell(cell)} advertises ${capabilityId}, but no geometry setter is available.`);
}

export function setConnectorStyle(link: joint.dia.Link, value: string): void {
    const kind = requireSupported(link, 'connectorStyle');
    if (kind !== 'flow') {
        throw new Error(`${kind} does not support connectorStyle.`);
    }
    if (!isAllowedValue(kind, 'connectorStyle', value)) {
        throw new Error(`Unsupported connectorStyle '${value}'.`);
    }
    if (!isConnectorStyleable(link)) {
        applyConnectorStyleMixin(link as joint.shapes.standard.Link);
    }
    if (!isConnectorStyleable(link)) {
        throw new Error('Flow link does not expose connector style behavior.');
    }
    link.setConnectorStyle(value as ConnectorStyle);
}

export function updateLinkTextLabel(link: joint.dia.Link, labelIndex: number, text: string): void {
    const labels = link.labels() ?? [];
    if (!Number.isInteger(labelIndex) || labelIndex < 0 || labelIndex >= labels.length) {
        throw new Error(`Label index ${labelIndex} does not exist.`);
    }
    if (isIconLabel(labels[labelIndex])) {
        throw new Error(`Label index ${labelIndex} is an icon label and cannot be edited as text.`);
    }
    link.label(labelIndex, text.length > 0
        ? { attrs: { text: { text }, rect: { visibility: 'unset' } } }
        : { attrs: { text: { text: '' }, rect: { visibility: 'hidden' } } });
}

export function removeLinkLabel(link: joint.dia.Link, labelIndex: number): void {
    const labels = link.labels() ?? [];
    if (!Number.isInteger(labelIndex) || labelIndex < 0 || labelIndex >= labels.length) {
        throw new Error(`Label index ${labelIndex} does not exist.`);
    }
    link.removeLabel(labelIndex);
}

export function addLinkTextLabel(link: joint.dia.Link, text: string): void {
    if (!text || text.length === 0) {
        throw new Error('A text label requires non-empty text.');
    }
    const labelCount = (link.labels() ?? []).length;
    const position = labelCount === 0 ? 0.5 : (labelCount + 1) / (labelCount + 2);
    link.appendLabel(createFlowLinkTextLabel(text, position));
}

export async function addLinkIconLabel(link: joint.dia.Link, iconName: string): Promise<void> {
    const kind = requireSupported(link, 'iconLabels');
    if (!isAllowedValue(kind, 'iconLabels', iconName)) {
        throw new Error(`Unsupported icon label '${iconName}'.`);
    }
    const { viewBox, d } = await fetchIconShape(iconName);
    const labelCount = (link.labels() ?? []).length;
    const position = labelCount === 0 ? 0.5 : (labelCount + 1) / (labelCount + 2);
    link.appendLabel({ position, ...createIconLabelMarkup(iconName, d, viewBox) });
}

export async function applyLinkLabelMutations(
    link: joint.dia.Link,
    mutations: FlowLinkLabelMutationDescriptor[] | null | undefined,
): Promise<void> {
    if (!mutations || mutations.length === 0) {
        return;
    }

    const labels = link.labels() ?? [];
    const addressedIndices = new Set<number>();
    for (const mutation of mutations) {
        if (mutation.type !== 'updateText' && mutation.type !== 'remove') {
            continue;
        }
        if (!Number.isInteger(mutation.index) || mutation.index < 0 || mutation.index >= labels.length) {
            throw new Error(`Label index ${mutation.index} does not exist.`);
        }
        if (addressedIndices.has(mutation.index)) {
            throw new Error(`Label index ${mutation.index} is patched more than once.`);
        }
        addressedIndices.add(mutation.index);
        if (mutation.type === 'updateText' && isIconLabel(labels[mutation.index])) {
            throw new Error(`Label index ${mutation.index} is an icon label and cannot be edited as text.`);
        }
    }

    for (const mutation of mutations) {
        if (mutation.type !== 'updateText') {
            continue;
        }
        const patch = readPatch(mutation.text, `labels[${mutation.index}]`);
        if (!patch.applied) {
            throw new Error(`labels[${mutation.index}] must specify a patch.`);
        }
        updateLinkTextLabel(link, mutation.index, patch.clear ? '' : String(patch.value));
    }

    const removals = mutations
        .filter((mutation): mutation is Extract<FlowLinkLabelMutationDescriptor, { type: 'remove' }> => mutation.type === 'remove')
        .sort((left, right) => right.index - left.index);
    for (const removal of removals) {
        removeLinkLabel(link, removal.index);
    }

    for (const mutation of mutations) {
        if (mutation.type === 'addText') {
            addLinkTextLabel(link, mutation.text);
        } else if (mutation.type === 'addIcon') {
            await addLinkIconLabel(link, mutation.iconName);
        }
    }
}

export async function applyFlowElementPatch(
    cell: joint.dia.Cell,
    patch: FlowElementPatchDescriptor | null | undefined,
): Promise<void> {
    if (!patch) {
        return;
    }

    const propertyPatches: Array<[string, PatchValue<string | number> | null | undefined]> = [
        ['primaryLabel', patch.text?.primaryLabel],
        ['secondaryLabel', patch.text?.secondaryLabel],
        ['text', patch.text?.text],
        ['backgroundColor', patch.presentation?.backgroundColor],
        ['textColor', patch.presentation?.textColor],
        ['borderColor', patch.presentation?.borderColor],
        ['shapeColor', patch.presentation?.shapeColor],
        ['iconName', patch.icon?.iconName],
        ['strokeColor', patch.line?.strokeColor],
        ['strokeWidth', patch.line?.strokeWidth],
    ];
    for (const [propertyName, rawPatch] of propertyPatches) {
        const valuePatch = readPatch(rawPatch, propertyName);
        if (!valuePatch.applied) {
            continue;
        }
        const canClear = propertyName === 'primaryLabel'
            || propertyName === 'secondaryLabel'
            || propertyName === 'text';
        if (valuePatch.clear && !canClear) {
            throw new Error(`${propertyName} cannot be cleared; set an explicit value instead.`);
        }
        await setElementProperty(cell, propertyName, valuePatch.clear ? '' : valuePatch.value as string | number);
    }

    // Position before size and tailTip matches the callout's manual behavior. An explicit tailTip
    // is therefore the final authority when several geometry fields are patched together.
    const geometryPatches: Array<[string, PatchValue<PointValue | SizeValue> | null | undefined]> = [
        ['position', patch.geometry?.position],
        ['size', patch.geometry?.size],
        ['tailTip', patch.geometry?.tailTip],
        ['sourcePoint', patch.geometry?.sourcePoint],
        ['targetPoint', patch.geometry?.targetPoint],
    ];
    for (const [propertyName, rawPatch] of geometryPatches) {
        const valuePatch = readPatch(rawPatch, propertyName);
        const value = requireSetValue(valuePatch, propertyName);
        if (value !== undefined) {
            setElementGeometry(cell, propertyName, value);
        }
    }

    if (patch.line?.labels) {
        requireSupported(cell, 'textLabels');
        if (!(cell instanceof DrawnLine)) {
            throw new Error(`${classifyCell(cell)} cannot receive line label patches.`);
        }
        await applyLinkLabelMutations(cell, patch.line.labels);
    }
}

export async function applyFlowLinkPatch(
    link: joint.dia.Link,
    patch: FlowLinkPatchDescriptor | null | undefined,
): Promise<void> {
    if (!patch) {
        return;
    }
    const connectorPatch = readPatch(patch.connectorStyle, 'connectorStyle');
    if (connectorPatch.applied) {
        setConnectorStyle(link, connectorPatch.clear ? ConnectorStyle.Sharp : String(connectorPatch.value));
    }
    if (patch.labels) {
        requireSupported(link, 'textLabels');
        await applyLinkLabelMutations(link, patch.labels);
    }
}
